import * as RxSelect from '@radix-ui/react-select';
import type { ReactNode } from 'react';

import Text from '../base/Text';
import ChevronDropdownIcon from '../icons/ChevronDropdownIcon';
import { input } from './Input';

export type SelectItem = {
  value: string | number;
  label: string | ReactNode;
  description?: string;
};

const ITEM_VARIANT_CLASS_NAMES = {
  blue: 'hover:bg-blue hover:text-white',
  subtle: 'hover:bg-gray-15 dark:hover:bg-gray-90',
} as const;

const ITEM_DESCRIPTION_VARIANT_CLASS_NAMES = {
  blue: 'text-gray-60 group-hover:text-white',
  subtle: 'text-gray-60',
} as const;

export default function Select({
  disabled = false,
  inModal = false,
  itemVariant = 'blue',
  items,
  value,
  onValueChange,
  className,
  placeholder,
  position = 'item-aligned',
  side = 'bottom',
  align = 'start',
  sideOffset = 8,
}: {
  disabled?: boolean;
  inModal?: boolean;
  itemVariant?: keyof typeof ITEM_VARIANT_CLASS_NAMES;
  items: Array<SelectItem>;
  value?: string | number;
  onValueChange?: (value: string) => void;
  className?: string;
  placeholder?: string;
  position?: 'item-aligned' | 'popper';
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
}) {
  const resolvedValue =
    value === undefined || value === null ? undefined : value.toString();

  return (
    <RxSelect.Root
      key={resolvedValue ?? '__cleared__'}
      disabled={disabled}
      value={resolvedValue}
      onValueChange={onValueChange}
    >
      <RxSelect.Trigger
        className={input({
          className: `flex w-full items-center justify-between ${className}`,
        })}
        aria-label="Food"
      >
        <RxSelect.Value
          placeholder={
            <Text variant="studio/body/medium/sm" className="text-gray-45">
              {placeholder}
            </Text>
          }
        />
        <RxSelect.Icon className="text-gray-45 dark:text-gray-60 relative left-1">
          <ChevronDropdownIcon />
        </RxSelect.Icon>
      </RxSelect.Trigger>
      <RxSelect.Portal>
        <RxSelect.Content
          position={position}
          side={position === 'popper' ? side : undefined}
          align={position === 'popper' ? align : undefined}
          sideOffset={position === 'popper' ? sideOffset : undefined}
          className={`shadow-menu dark:bg-gray-75 relative ${
            inModal ? 'z-[70]' : 'z-10'
          } overflow-hidden rounded-2xl bg-white ${
            position === 'popper' ? 'min-w-[var(--radix-select-trigger-width)]' : ''
          }`}
        >
          <RxSelect.Viewport className="p-2">
            <RxSelect.Group>
              {items.map((item) => (
                <RxSelect.Item
                  key={item.value}
                  className={`outline-hidden group flex min-h-10 cursor-pointer flex-col items-start justify-center gap-0.5 rounded-lg px-4 py-2 ${ITEM_VARIANT_CLASS_NAMES[itemVariant]}`}
                  value={item.value.toString()}
                >
                  <div className="flex w-full flex-1 items-center justify-between gap-2">
                    <RxSelect.ItemText>
                      {typeof item.label === 'string' ? (
                        <Text variant="studio/body/medium/sm">{item.label}</Text>
                      ) : (
                        item.label
                      )}
                    </RxSelect.ItemText>
                    <RxSelect.ItemIndicator className="SelectItemIndicator">
                      <Dot />
                    </RxSelect.ItemIndicator>
                  </div>
                  {item.description && (
                    <Text
                      variant="studio/body/sm"
                      className={ITEM_DESCRIPTION_VARIANT_CLASS_NAMES[itemVariant]}
                    >
                      {item.description}
                    </Text>
                  )}
                </RxSelect.Item>
              ))}
            </RxSelect.Group>
          </RxSelect.Viewport>
        </RxSelect.Content>
      </RxSelect.Portal>
    </RxSelect.Root>
  );
}

const Dot = () => {
  return (
    <svg
      width="6"
      height="6"
      viewBox="0 0 6 6"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="3" cy="3" r="3" />
    </svg>
  );
};
